import { useApplicantAPI, useRequest } from "@certn/api";
import type { components } from "@certn/api/generated-schemas/applicant-portal";
import {
  ButtonGroup,
  Modal,
  Dialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
  Select,
  SelectItem,
  PrimaryButton,
  showToast,
} from "@certn/cascadia-react";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useApplicationFormContext } from "../../ApplicationFormContext";

type DemoResponseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
};

export function DemoSkipModal({
  isOpen,
  onClose,
  onSave,
}: DemoResponseModalProps) {
  const [demoIsSubmitting, setDemoIsSubmitting] = useState(false);
  const request = useRequest();

  const { caseId } = useApplicationFormContext();

  const { t } = useTranslation();
  const identityOptionsPath =
    `/api/internal/applicant/case/${caseId}/one-id/demo-skip-options/` as "/api/internal/applicant/case/{id}/one-id/demo-skip-options/";
  const visaOptionsPath =
    `/api/internal/applicant/case/${caseId}/au-right-to-work/visa-scenario-options/` as "/api/internal/applicant/case/{id}/au-right-to-work/visa-scenario-options/";

  const { data: identityOptions, isLoading: identityOptionsLoading } =
    useApplicantAPI(isOpen ? identityOptionsPath : null);
  const { data: visaOptions, isLoading: visaOptionsLoading } = useApplicantAPI(
    isOpen ? visaOptionsPath : null
  );

  const [selectedIdentity, setSelectedIdentity] = useState("");
  const [selectedVisaScenario, setSelectedVisaScenario] = useState("");

  const visaOptionsReady = !visaOptionsLoading;
  const showVisaScenarioSelect =
    visaOptionsReady && (visaOptions?.length ?? 0) > 0;
  const canSubmit =
    Boolean(selectedIdentity) &&
    visaOptionsReady &&
    (!showVisaScenarioSelect || Boolean(selectedVisaScenario)) &&
    !demoIsSubmitting;

  useEffect(() => {
    if (!isOpen) {
      setSelectedIdentity("");
      setSelectedVisaScenario("");
      setDemoIsSubmitting(false);
    }
  }, [isOpen]);

  const onPress = async () => {
    setDemoIsSubmitting(true);
    try {
      const body: components["schemas"]["OneIDDemoSkip"] = {
        response: selectedIdentity,
      };
      if (selectedVisaScenario) {
        body.au_visa_scenario = selectedVisaScenario;
      }
      await request(
        `/api/internal/applicant/case/${caseId}/one-id/demo-skip/`,
        {
          method: "POST",
          body: JSON.stringify(body),
        }
      );
      // Legacy OneIdControl relies on this delay so the mocked webhooks can
      // land before the form is submitted. Only treat the skip as saved when
      // the POST succeeded — a failure used to toast and then call onSave.
      setTimeout(() => {
        onSave();
      }, 3000);
    } catch (e) {
      console.warn("Unable to call demo skip api:", e);
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
      setDemoIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen>
      <Dialog>
        <DialogHeader onClose={onClose}>{t("Demo Response")}</DialogHeader>
        <DialogContent>
          <Select
            isLoading={identityOptionsLoading}
            testID="skip-demo-select-options"
            isRequired
            label="Select identity result"
            onSelectionChange={(key) =>
              setSelectedIdentity(key?.toString() ?? "")
            }
            items={identityOptions}
            selectedKey={selectedIdentity}
          >
            {identityOptions?.map((option) => (
              <SelectItem
                id={option.value}
                textValue={option.display_value}
                key={option.value}
              >
                {option.display_value}
              </SelectItem>
            ))}
          </Select>
          {showVisaScenarioSelect && (
            <Select
              isLoading={visaOptionsLoading}
              testID="skip-demo-select-visa-scenario"
              isRequired
              label="Select visa scenario"
              onSelectionChange={(key) =>
                setSelectedVisaScenario(key?.toString() ?? "")
              }
              items={visaOptions}
              selectedKey={selectedVisaScenario}
            >
              {visaOptions?.map((option) => (
                <SelectItem
                  id={option.value}
                  textValue={option.display_value}
                  key={option.value}
                >
                  {option.display_value}
                </SelectItem>
              ))}
            </Select>
          )}
        </DialogContent>
        <DialogFooter>
          <ButtonGroup>
            <PrimaryButton
              testID="skip-modal-next-step-button"
              size="medium"
              onPress={onPress}
              isLoading={demoIsSubmitting}
              isDisabled={!canSubmit}
            >
              <Trans>Next step</Trans>
            </PrimaryButton>
          </ButtonGroup>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
